/**
 *
 * @file experience.ts
 *
 * @description This file contains the experience data for the portfolio website.
 *  - experiences: An array of objects containing the developer's work experiences.
 *
 */

import type { StaticImageData } from "next/image";

import { ciccc, cde, bladeclub, cozycot, nyp } from "@/assets";

export type ExperienceEntry = {
	id: string;
	title: string;
	company_name: string;
	location: string;
	icon: StaticImageData;
	iconBg: string;
	date: string;
	mission: string;
	impact: string;
	stack: string[];
	points: string[];
};

export const experiences: ExperienceEntry[] = [
	{
    id: crypto.randomUUID(),
    title: "Senior Software Engineer",
    company_name: "Cornerstone Digital Data Ltd.",
    location: "Vancouver, BC · Hybrid",
    icon: cde,
    iconBg: "#E6DEDD",
    date: "Jun 2024 - Present",
    mission:
      "Architecting and delivering full-stack educational technology solutions, from student management systems to enterprise-scale college websites, driving digital transformation across multiple campuses.",
    impact:
      "Built production systems serving 3000+ students across multiple campuses including automated document generation, AI-powered team collaboration tools, intelligent job matching platforms, and comprehensive financial management systems.",
    stack: [
      "Next.js",
      "Turborepo",
      "Vue.js",
      "Convex",
      "TypeScript",
      "React",
      "Supabase",
      "Clerk",
      "TailwindCSS",
      "Stripe",
      "Claude AI",
      "Vite",
      "Express.js",
      "MySQL",
      "CI/CD"
    ],
    points: [
      "Architected enterprise monorepo (CICCC Website) with Next.js 15, Convex, and Turborepo, implementing 5-language support, admin CMS, Stripe payment integration, and AMP Educator LMS connectivity for multi-campus operations.",
      "Engineered student document system (CornerDocs) with Vue.js and Express/MySQL backend, generating PDFs for transcripts, confirmations, and invoices with real-time course tracking and authentication via Clerk.",
      "Built AI-powered team standup application (CDE Huddle) with automated weekly report generation using Claude AI, Supabase Edge Functions, and PostgreSQL scheduled jobs, featuring rich text editing and export capabilities.",
      "Developed intelligent job board platform (GoJobs) with Next.js and Convex featuring AI-powered job matching, automated email notifications via Brevo, Google Sheets integration, and comprehensive analytics dashboard.",
      "Created financial management platform (EduFinance) with Next.js integrating AMP Educator API, Supabase sync tracking, and data visualization using React Query and TanStack Table for multi-campus financial oversight."
    ],
  },
	{
		id: crypto.randomUUID(),
		title: "Web Application Development Instructor",
		company_name: "CICCC - Cornerstone International Community College of Canada",
		location: "Vancouver, BC",
		icon: ciccc,
		iconBg: "#E6DEDD",
		date: "May 2020 - Feb 2025",
		mission:
			"Orchestrating comprehensive web development education while spearheading real-world project initiatives that bridge theoretical learning with practical client collaboration.",
		impact:
			"Engineered innovative in-house applications that reduced manual workload by 80% and empowered students with hands-on experience working directly with clients on live projects.",
		stack: ["Next.js", "React", "Node.js", "Django", "GraphQL", "Gatsby.js", "React-Email", "SendinBlue API"],
		points: [
			"Taught comprehensive web development curriculum from HTML/CSS/JavaScript to React, Node.js, and Django.",
			"Facilitated student-client collaborations on real projects for hands-on experience.",
			"Introduced modern tools like Gatsby.js, GraphQL, and Next.js to keep curriculum current.",
			"Built Next.js app with React-Email reducing manual workload by 80%, integrated SendinBlue API and Excel uploads.",
		],
	},
	{
		id: crypto.randomUUID(),
		title: "Software Systems Developer",
		company_name: "Blade Club Singapore",
		location: "Singapore",
		icon: bladeclub,
		iconBg: "#E6DEDD",
		date: "Feb 2009 - Aug 2018",
		mission:
			"Orchestrating full-stack development initiatives across web platforms, mobile applications, and educational systems to enhance operational efficiency and user engagement.",
		impact:
			"Pioneered the development of a robust student management application tracking attendance, skills progression, and analytical data for 300+ fencing students, fostering a transition to a sustainable, paperless environment.",
		stack: ["Ruby on Rails", "WordPress", "Joomla", "Moodle", "GameSalad", "LUA", "Titanium", "Ionic", "Postman"],
		points: [
			"Built in-house websites using WordPress, Joomla, and Moodle platforms.",
			"Developed Ruby on Rails student management app tracking 300+ fencing students' attendance and progress.",
			"Built native clinic mobile app with Titanium and Ionic, tested RESTful APIs with Postman.",
			"Designed UI graphics for racing game interface.",
			"Conducted IT security and programming workshops for children.",
		],
	},
	{
		id: crypto.randomUUID(),
		title: "Graphic Designer",
		company_name: "CozyCot Pte Ltd",
		location: "Singapore",
		icon: cozycot,
		iconBg: "#E6DEDD",
		date: "2008",
		mission:
			"Contributing to the vibrant world of online beauty, fashion, lifestyle, and shopping at CozyCot, a predominantly women-centric portal, through creative visual design and content curation.",
		impact:
			"Crafted visually appealing online banners, meticulously edited videos, and curated landing pages and microsites, tailoring each element to specific client requests while enhancing overall brand aesthetic and engagement.",
		stack: ["Photoshop", "Illustrator", "InDesign", "Premiere Pro", "ActionScript", "Flash"],
		points: [
			"Designed online banners, edited videos, and created landing pages and microsites for clients.",
			"Used Adobe Photoshop, Illustrator, and InDesign for creative projects.",
			"Created content design for in-house advertising in magazines and web pages.",
			"Photographed events and workshops for brand content.",
		],
	},
	{
		id: crypto.randomUUID(),
		title: "Development Technologist",
		company_name: "Nanyang Polytechnic",
		location: "Singapore",
		icon: nyp,
		iconBg: "#E6DEDD",
		date: "2007 - 2008",
		mission:
			"Playing a pivotal role in Industrial Project Development, collaborating with external companies to explore cutting-edge technologies and guide students through innovative projects.",
		impact:
			"Spearheaded a pioneering initiative testing the feasibility and functionality of touchless interactive technology known as Ubiq' windows, while providing mentorship to Final Year Project students.",
		stack: ["C#", "JavaScript", "Java", "HTML", "CSS"],
		points: [
			"Collaborated with external companies on Industrial Project Development initiatives.",
			"Tested touchless interactive technology (Ubiq' windows) feasibility and functionality.",
			"Guided Final Year Project students through their academic projects.",
			"Managed laboratory administrative tasks and maintenance.",
		],
	},
];
